/**
 * FX markup applied on top of the spot rate for all cross-currency conversions.
 * Represents the typical credit-card / bank foreign-exchange fee (2%).
 * The spot rate is stored separately on each expense doc for transparency.
 */
export const FX_MARKUP = 0.03;

const CACHE: Record<string, number> = {};

// Hardcoded fallback rates (approximate, based on USD as 1)
const HARD_FALLBACK: Record<string, number> = {
  "USD": 1, "EUR": 0.92, "GBP": 0.79, "JPY": 150.5, "AUD": 1.52, "CAD": 1.35,
  "CHF": 0.88, "CNY": 7.20, "HKD": 7.82, "SGD": 1.34, "NZD": 1.62, "KRW": 1332,
  "INR": 82.9, "MXN": 17.1, "BRL": 4.95, "ZAR": 19.1, "THB": 35.8, "MYR": 4.75,
  "IDR": 15700, "PHP": 56.1, "DKK": 6.85, "HUF": 360, "CZK": 23, "PLN": 3.95,
  "RON": 4.55, "SEK": 10.3, "ISK": 138, "NOK": 10.5, "TRY": 31.2
};

export async function getExchangeRate(from: string, to: string, date?: string): Promise<number> {
  if (from === to) return 1;
  
  // Format date for Frankfurter API (YYYY-MM-DD)
  const dateKey = date ? date.split('T')[0] : 'latest';
  const cacheKey = `${from}-${to}-${dateKey}`;
  
  if (CACHE[cacheKey]) return CACHE[cacheKey];

  // Prevent future-date requests to Frankfurter (which would 404).
  // Compare against today rather than a hardcoded year so we don't silently
  // fall back to "latest" rates for historical dates as the calendar advances.
  const isFuture = dateKey !== 'latest' &&
    new Date(dateKey + 'T00:00:00Z').getTime() > Date.now();
  const effectiveDate = isFuture ? 'latest' : dateKey;

  try {
    // Primary: Frankfurter (supports historical)
    const response = await fetch(`https://api.frankfurter.app/${effectiveDate}?from=${from}&to=${to}`);
    if (response.ok) {
      const data = await response.json();
      const rate = data.rates?.[to];
      if (typeof rate === 'number' && Number.isFinite(rate) && rate > 0) {
        CACHE[cacheKey] = rate;
        return rate;
      }
    }
    throw new Error('Frankfurter failed');
  } catch (err) {
    console.warn("Primary Currency API Failed, trying secondary...", err);
    
    try {
      // Secondary: ExchangeRate-API (stable, latest only)
      const secResponse = await fetch(`https://open.er-api.com/v6/latest/${from}`);
      if (secResponse.ok) {
        const data = await secResponse.json();
        const rate = data.rates?.[to];
        if (typeof rate === 'number' && Number.isFinite(rate) && rate > 0) {
          CACHE[cacheKey] = rate;
          return rate;
        }
      }
    } catch (secErr) {
      console.warn("Secondary Currency API Failed, using hard fallback", secErr);
    }

    // Tertiary: Hardcoded static fallback
    const fromRate = HARD_FALLBACK[from] || 1;
    const toRate = HARD_FALLBACK[to] || 1;
    const finalRate = (1 / fromRate) * toRate;
    // An offline estimate must not prevent a later request from recovering.
    return finalRate;
  }
}

// Common travel currencies supported by Frankfurter
export const SUPPORTED_CURRENCIES = Object.keys(HARD_FALLBACK);
